// Network layer for the Ask Bizminer panel. Talks to:
//   - api.app   POST {VUE_APP_API_BASE_URL}/chat/assertion  (axios, like the rest of the app)
//   - chat.bizminer.com  {VUE_APP_CHAT_BASE_URL}  (fetch, credentials: 'include', HttpOnly cookie)
// Contract: chat.bizminer.com README (PFD-215/216; limits PFD-218, share PFD-220).
import axios from 'axios';
// The .js extension keeps this module loadable by `node --test` (webpack is fine with it).
import { createSseParser, conversationsQuery, normalizeConversationList } from './chatCore.js';

export class ChatApiError extends Error {
  // cardStatus: the card's server-side state from a 409 confirmation_closed
  // ({error, status}): declined | expired | redeeming | redeemed.
  // retryable: a 403 subscription_required with `retryable: true` = the service
  // could not read the subscription (try again), not a lapsed one.
  // fields: a 400 invalid_inputs body's `{path: message}`; version: the notice
  // version of a 403 notice_required / 400 unknown_version (CHAT-11).
  // resetAt / cartUrl: a 429 daily_message_limit / daily_purchase_limit body (CHAT-06):
  // when the limit lifts (ISO) and, for purchases, the app's cart link.
  constructor(status, code, message, cardStatus, retryable, fields, version, resetAt, cartUrl) {
    super(message || code || 'chat_error');
    this.status = status;
    this.code = code || 'error';
    this.cardStatus = cardStatus || null;
    this.retryable = retryable === true;
    this.fields = fields || null;
    this.version = version || null;
    this.resetAt = resetAt || null;
    this.cartUrl = cartUrl || null;
  }
}

// The chat service or api.app says this account may not use chat: hide every
// entry point (chat/deny). not_in_beta = chat.bizminer.com's beta list,
// not_allowed = api.app's assertion refusing BVR / disabled accounts.
export function isDenied(e) {
  return e instanceof ChatApiError && (e.code === 'not_in_beta' || e.code === 'not_allowed');
}

const chatBase = () => String(process.env.VUE_APP_CHAT_BASE_URL || '').replace(/\/+$/, '');

// No base URL configured = the feature does not exist in this build.
export const chatConfigured = () => chatBase() !== '';

async function readError(res) {
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  return new ChatApiError(
    res.status,
    body && body.error,
    body && body.message,
    body && typeof body.status === 'string' ? body.status : null,
    body && body.retryable === true,
    body && body.fields && typeof body.fields === 'object' && !Array.isArray(body.fields) ? body.fields : null,
    body && typeof body.version === 'string' ? body.version : null,
    body && typeof body.resetAt === 'string' ? body.resetAt : null,
    body && typeof body.cartUrl === 'string' ? body.cartUrl : null
  );
}

async function chatFetch(path, { method = 'GET', body, headers, signal, keepalive } = {}) {
  let res;
  try {
    res = await fetch(chatBase() + path, {
      method,
      credentials: 'include',
      headers: Object.assign({ Accept: 'application/json' }, body !== undefined ? { 'Content-Type': 'application/json' } : {}, headers),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
      keepalive,
    });
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    throw new ChatApiError(0, 'connection', 'Could not reach Ask Bizminer.');
  }
  return res;
}

// 1. A short-lived assertion from api.app, minted from the app's own session.
export async function fetchAssertion() {
  const sessionId = localStorage.getItem('vue-authenticate.vueauth_access_token');
  if (!sessionId) throw new ChatApiError(401, 'no_app_session', 'You are signed out.');
  let res;
  try {
    res = await axios.post(`${process.env.VUE_APP_API_BASE_URL}/chat/assertion`, { session_id: sessionId });
  } catch (e) {
    const status = e && e.response ? e.response.status : 0;
    const body = e && e.response ? e.response.data : null;
    // 403 {error:'not_allowed'}: BVR or disabled account. Not a glitch: do not retry, hide the entry points.
    if (status === 403 && body && body.error === 'not_allowed') {
      throw new ChatApiError(403, 'not_allowed', 'Ask Bizminer is not available for this account.');
    }
    throw new ChatApiError(status, 'assertion_failed', 'Could not start Ask Bizminer.');
  }
  const a = res && res.data && res.data.assertion;
  if (!a) throw new ChatApiError(401, 'assertion_failed', 'Could not start Ask Bizminer.');
  return a;
}

// 2. Exchange it for the chat cookie. Done on EVERY panel open and on
// reauth_required. Returns {ok, uid, subUser}.
export async function openSession() {
  const assertion = await fetchAssertion();
  const res = await chatFetch('/session', { method: 'POST', body: { assertion } });
  if (!res.ok) throw await readError(res);
  return res.json();
}

// On app logout. Best effort: never blocks or throws.
export function closeSession() {
  if (!chatConfigured()) return Promise.resolve();
  return chatFetch('/session', { method: 'DELETE', keepalive: true }).then(
    () => undefined,
    () => undefined
  );
}

export async function getMe() {
  const res = await chatFetch('/me');
  if (!res.ok) throw await readError(res);
  return res.json();
}

export async function createConversation(context, signal) {
  const res = await chatFetch('/conversations', { method: 'POST', body: context ? { context } : {}, signal });
  if (!res.ok) throw await readError(res);
  return res.json();
}

export async function getConversation(id, signal) {
  const res = await chatFetch('/conversations/' + encodeURIComponent(id), { signal });
  if (!res.ok) throw await readError(res);
  return res.json();
}

// GET /conversations (CHAT-13/CHAT-07): one page of the caller's history, newest
// first. `cursor` = the previous page's nextCursor; `q` = server-side search.
// Resolves {items, nextCursor, emptyState}.
export async function listConversations({ cursor, q, limit, signal } = {}) {
  const res = await chatFetch('/conversations' + conversationsQuery({ cursor, q, limit }), { signal });
  if (!res.ok) throw await readError(res);
  return normalizeConversationList(await res.json());
}

// DELETE /conversations/:id. Resolves on 204. Rejects with ChatApiError: 404
// not_found (already gone), 409 conversation_busy (an answer or purchase is
// running: nothing deleted), 401 reauth_required (see withReauth).
export async function deleteConversation(id, signal) {
  const res = await chatFetch('/conversations/' + encodeURIComponent(id), { method: 'DELETE', signal });
  if (!res.ok) throw await readError(res);
}

// POST /conversations/:id/share (CHAT-08): share it with Bizminer support. Resolves
// {id, sharedAt, shareNote}. A repeat is fine. Rejects with ChatApiError: 404 not_found,
// 401 reauth_required (see withReauth).
export async function shareConversation(id, signal) {
  const res = await chatFetch('/conversations/' + encodeURIComponent(id) + '/share', { method: 'POST', body: {}, signal });
  if (!res.ok) throw await readError(res);
  return res.json();
}

// DELETE /conversations/:id/share: stop sharing. Resolves on 204. Rejects like shareConversation.
export async function unshareConversation(id, signal) {
  const res = await chatFetch('/conversations/' + encodeURIComponent(id) + '/share', { method: 'DELETE', signal });
  if (!res.ok) throw await readError(res);
}

async function readStream(res, onEvent) {
  const parser = createSseParser(onEvent);
  if (!res.body || !res.body.getReader) {
    parser.push(await res.text());
    parser.flush();
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(dec.decode(value, { stream: true }));
  }
  parser.push(dec.decode());
  parser.flush();
}

// POST /conversations/:id/messages (SSE). Resolves when the stream ends.
// Rejects with ChatApiError for a non-200 start (401 reauth_required, 403
// not_in_beta, 409 turn_in_progress, 404...) and with ChatApiError(0,
// 'connection') if the stream drops mid-way.
export async function sendMessage(conversationId, text, onEvent, signal) {
  const res = await chatFetch('/conversations/' + encodeURIComponent(conversationId) + '/messages', {
    method: 'POST',
    body: { text },
    headers: { Accept: 'text/event-stream' },
    signal,
  });
  if (!res.ok) throw await readError(res);
  try {
    await readStream(res, onEvent);
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    throw new ChatApiError(0, 'connection', 'The connection was interrupted.');
  }
}

// Confirm / decline a pending purchase (PFD-217). A started click ALWAYS answers
// HTTP 200 with an SSE stream: the real outcome is the `confirm_result` event
// (or an `error` event), never the status code. A click that cannot start
// rejects with ChatApiError: 404 not_found, 409 turn_in_progress, 409
// confirmation_closed (cardStatus = where the card stands), 401 reauth_required.
// A stream that drops mid-way rejects with ChatApiError(0, 'connection'), the
// same as sendMessage: the caller must then re-read the card from the server.
export async function answerConfirmation(conversationId, pendingId, action, onEvent, signal) {
  const path = '/conversations/' + encodeURIComponent(conversationId) + '/confirmations/' + encodeURIComponent(pendingId) + '/' + action;
  const res = await chatFetch(path, { method: 'POST', body: {}, headers: { Accept: 'text/event-stream' }, signal });
  if (!res.ok) throw await readError(res);
  try {
    await readStream(res, onEvent);
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    throw new ChatApiError(0, 'connection', 'The connection was interrupted.');
  }
}

// GET /notice-acks (CHAT-11/a): the current financial-data notice and whether
// this user acknowledged it. Resolves {version, kind, title, text, actions, acknowledged}.
export async function getNoticeAck(signal) {
  const res = await chatFetch('/notice-acks', { signal });
  if (!res.ok) throw await readError(res);
  return res.json();
}

// POST /notice-acks {version} ("Continue" on the notice). A repeat is fine.
// Rejects with ChatApiError: 400 unknown_version (version = the current one),
// 401 reauth_required (see withReauth).
export async function postNoticeAck(version, signal) {
  const res = await chatFetch('/notice-acks', { method: 'POST', body: { version }, signal });
  if (!res.ok) throw await readError(res);
  return res.json();
}

// POST /conversations/:id/forms/:formId/submit (CHAT-11/b, CHAT-22). SSE like a
// message: form_result, then text + confirm (a first run) or citation + text +
// ownership (an owned report), then done. Rejects with ChatApiError before any
// stream starts: 400 invalid_inputs (`fields`), 403 notice_required (`version`),
// 404, 409 turn_in_progress, 429, 401 reauth_required. A stream that drops
// mid-way rejects with ChatApiError(0, 'connection'): the caller re-reads the
// conversation.
export async function submitForm(conversationId, formId, values, onEvent, signal) {
  const path = '/conversations/' + encodeURIComponent(conversationId) + '/forms/' + encodeURIComponent(formId) + '/submit';
  const res = await chatFetch(path, { method: 'POST', body: { values }, headers: { Accept: 'text/event-stream' }, signal });
  if (!res.ok) throw await readError(res);
  try {
    await readStream(res, onEvent);
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    throw new ChatApiError(0, 'connection', 'The connection was interrupted.');
  }
}

// GET /conversations/:id/messages/:messageId/summary (CHAT-11, Lender demo): the
// one-page client summary of an answer that cited a Financial / Valuation Report.
// Only ever called from a click. 404 no_report | not_found.
export async function getSummary(conversationId, messageId, signal) {
  const path = '/conversations/' + encodeURIComponent(conversationId) + '/messages/' + encodeURIComponent(messageId) + '/summary';
  const res = await chatFetch(path, { signal });
  if (!res.ok) throw await readError(res);
  return res.json();
}

// POST /insights (CHAT-20): "What stands out?" for one owned report. Only ever
// called from a click. Rejects with ChatApiError: 403 subscription_required, 409
// not_owned | report_mismatch, 400, 429 too_many_turns | insights_limit, 401
// reauth_required (see withReauth), 502/503. The service bounds the call at 60 s;
// the client gives up a little later.
export const INSIGHTS_TIMEOUT_MS = 75000;
export async function postInsights(body, signal) {
  const ctl = new AbortController();
  const onAbort = () => ctl.abort();
  if (signal) signal.addEventListener('abort', onAbort);
  const timer = setTimeout(() => ctl.abort(), INSIGHTS_TIMEOUT_MS);
  try {
    const res = await chatFetch('/insights', { method: 'POST', body, signal: ctl.signal });
    if (!res.ok) throw await readError(res);
    return await res.json();
  } catch (e) {
    if (e instanceof ChatApiError) throw e;
    throw new ChatApiError(0, 'connection', 'Could not reach Ask Bizminer.');
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

// Runs fn(); on 401 reauth_required re-does the session handshake ONCE,
// silently, and retries fn once.
export async function withReauth(fn) {
  try {
    return await fn();
  } catch (e) {
    if (e instanceof ChatApiError && e.status === 401 && e.code === 'reauth_required') {
      await openSession();
      return fn();
    }
    throw e;
  }
}
